import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const heroMedia = {
  mobile: '/images/quadtrip-hero-mobile-optimized.webp',
  desktop: '/images/quadtrip-hero-desktop-optimized.webp',
  video: '/videos/quadtrip-hero-loop-720p.mp4',
  fallback: '/images/wypozyczalnia-quadow-szczecin-quadtrip-no-bg.webp',
  animate: true,
};

export function HeroMedia({ bookingReady = false, obscured = false }: { bookingReady?: boolean; obscured?: boolean }) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const [imageSource, setImageSource] = useState<'poster' | 'fallback' | 'none'>('poster');
  const [allowVideo, setAllowVideo] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [paused, setPaused] = useState(false);
  const [loadVideo, setLoadVideo] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!heroMedia.animate) return;
    const desktop = window.matchMedia('(min-width: 768px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = 'connection' in navigator ? navigator.connection : undefined;
    const update = () => {
      const saveData = typeof connection === 'object' && connection !== null
        && 'saveData' in connection && connection.saveData === true;
      const slowConnection = typeof connection === 'object' && connection !== null
        && 'effectiveType' in connection && ['slow-2g', '2g', '3g'].includes(String(connection.effectiveType));
      const video = videoRef.current;
      const fullyBuffered = !!video && Number.isFinite(video.duration) && video.duration > 0
        && video.buffered.length === 1 && video.buffered.start(0) <= 0.1 && video.buffered.end(0) >= video.duration - 0.1;
      setAllowVideo(desktop.matches && !reducedMotion.matches && !saveData && (!slowConnection || fullyBuffered));
    };
    update();
    desktop.addEventListener('change', update);
    reducedMotion.addEventListener('change', update);
    if (connection instanceof EventTarget) connection.addEventListener('change', update);
    return () => {
      desktop.removeEventListener('change', update);
      reducedMotion.removeEventListener('change', update);
      if (connection instanceof EventTarget) connection.removeEventListener('change', update);
    };
  }, []);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!allowVideo) { setLoadVideo(false); setVideoReady(false); return; }
    if (!bookingReady || videoFailed || obscured || !visible || loadVideo) return;
    let idle: number | undefined;
    let fallback: ReturnType<typeof setTimeout> | undefined;
    let frame: number | undefined;
    const cancel = () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      if (idle !== undefined) window.cancelIdleCallback(idle);
      clearTimeout(fallback);
    };
    const schedule = () => {
      cancel();
      if (document.hidden) return;
      frame = requestAnimationFrame(() => {
        const start = () => { if (!document.hidden) setLoadVideo(true); };
        if ('requestIdleCallback' in window) {
          idle = window.requestIdleCallback(start, { timeout: 2000 });
        } else fallback = setTimeout(start, 500);
      });
    };
    const onVisible = () => {
      if (document.hidden) cancel();
      else schedule();
    };
    schedule();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      cancel();
    };
  }, [allowVideo, bookingReady, videoFailed, obscured, visible, loadVideo]);

  const handleVideoError = useCallback((error: unknown) => {
    console.warn('Hero video unavailable; using the static poster', error);
    setVideoFailed(true);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!allowVideo || !loadVideo || videoFailed || !video) return;
    let active = true;
    let playbackRequest = 0;
    const update = () => {
      const request = ++playbackRequest;
      if (paused || document.hidden || obscured || !visible) {
        video.pause();
      } else {
        void video.play().catch(error => { if (active && request === playbackRequest) handleVideoError(error); });
      }
    };
    update();
    document.addEventListener('visibilitychange', update);
    return () => {
      active = false;
      document.removeEventListener('visibilitychange', update);
      video.pause();
    };
  }, [allowVideo, loadVideo, videoFailed, paused, obscured, visible, handleVideoError]);

  return (
    <>
      <div ref={heroRef} className={`hero-media${heroMedia.animate ? ' hero-media--animated' : ''}`}>
        {imageSource !== 'none' && (
          <picture>
            {imageSource === 'poster' && <source media="(max-width: 767px)" srcSet={heroMedia.mobile} width={176} height={312} />}
            {imageSource === 'poster' && <source media="(min-width: 768px)" srcSet={heroMedia.desktop} width={1280} height={720} />}
            <Image src={imageSource === 'poster' ? heroMedia.desktop : heroMedia.fallback} alt=""
              width={1280} height={720} unoptimized loading="eager"
              onError={event => {
                console.warn('Hero image unavailable', event.currentTarget.currentSrc);
                setImageSource(imageSource === 'poster' ? 'fallback' : 'none');
              }} />
          </picture>
        )}
        {allowVideo && loadVideo && !videoFailed && (
          <video ref={videoRef} className={videoReady ? 'is-ready' : undefined}
            muted loop playsInline preload="none" poster={heroMedia.desktop} aria-hidden="true" tabIndex={-1}
            onPlaying={() => setVideoReady(true)}
            onError={event => handleVideoError(event.currentTarget.error)}>
            <source src={heroMedia.video} type="video/mp4"
              onError={() => handleVideoError(new Error(`Failed to load ${heroMedia.video}`))} />
          </video>
        )}
      </div>
      {allowVideo && loadVideo && !videoFailed && (videoReady || paused) && (
        <button type="button" className="hero-control" onClick={() => setPaused(value => !value)}
          aria-label={t(paused ? 'booking.heroPlay' : 'booking.heroPause')}
          title={t(paused ? 'booking.heroPlay' : 'booking.heroPause')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d={paused ? 'M8 5v14l11-7z' : 'M6 5h4v14H6zM14 5h4v14h-4z'} />
          </svg>
        </button>
      )}
    </>
  );
}
